import { Injectable } from '@angular/core'

import { UtilityService } from '../../utility-service/utility-service.service'
import { FilterSelectionInterpreterUtility } from '../filter-selection-interpreter-utility'
import { FundRuleName } from '../rules/fund-rule-name'
import { FilterGroup } from './filter-group-enum'
import { FilterGroupStateWrapper } from './filter-group-state-wrapper'
import { FilterNode } from './filter-node'
import { IFilterState } from './ifilter-state'
import { UnmodifiableFilterStateWrapper } from './unmodifiable-filter-state-wrapper'

@Injectable({
  providedIn: 'root',
})
export class FilterState {
  private consolidatedFilterStateMap: Map<FilterGroup, string[]> = new Map<
    FilterGroup,
    string[]
  >()
  private _uiFilterSelectionMap: Map<FilterGroup, string[]> = new Map<
    FilterGroup,
    string[]
  >()
  private selectedDisplayType: string
  private latestFilterSelection: string
  private isBenchMarkSelected: boolean
  private isAllShareClassSelected: boolean
  public overrideShareClassFlag = false
  constructor() {
    this.initializeAssetFilterSelection()
  }

  private initializeAssetFilterSelection() {
    this.consolidatedFilterStateMap.set(FilterGroup.AssetClasFilterGroup, [
      FundRuleName.EQUITY_US,
      FundRuleName.EQUITY_LARGE,
      FundRuleName.EQUITY_MID,
      FundRuleName.EQUITY_SMALL,
      FundRuleName.EQUITY_VALUE,
      FundRuleName.EQUITY_BLEND,
      FundRuleName.EQUITY_GROWTH,
      FundRuleName.EQUITY_INTERNATIONAL,
      FundRuleName.EQUITY_NON_US,
      FundRuleName.EQUITY_DEVELOPED,
      FundRuleName.EQUITY_EMERGING,
      FundRuleName.EQUITY_INDEX,
      FundRuleName.EQUITY_ACTIVE,
      FundRuleName.EQUITY_GLOBAL,
      FundRuleName.FI_US,
      FundRuleName.FI_INTERNATIONAL,
      FundRuleName.FI_DEVELOPED,
      FundRuleName.FI_EMERGING,
      FundRuleName.FI_GLOBAL,
      FundRuleName.FI_MULTI_SECTOR,
      FundRuleName.FI_NON_US,
      FundRuleName.FI_LONG,
      FundRuleName.FI_INTERMEDIATE,
      FundRuleName.FI_SHORT,
      FundRuleName.FI_BELOW_INV_GRADE,
      FundRuleName.FI_INV_GRADE_CORP,
      FundRuleName.FI_MUNI,
      FundRuleName.FI_TREASURY_AGENCY,
      FundRuleName.FI_INDEX,
      FundRuleName.FI_ACTIVE,
      FundRuleName.MULTI_ASSET_TARGET_DATE,
      FundRuleName.MULTI_ASSET_BALANCE,
      FundRuleName.MULTI_ASSET_TARGET_ALLOCATION,
      FundRuleName.MULTI_ASSET_US,
      FundRuleName.MULTI_ASSET_INTL,
      FundRuleName.CASH_EQUIVALENTS_TAXABLE,
      FundRuleName.CASH_EQUIVALENTS_MUNI,
      FundRuleName.CASH_EQUIVALENTS_STABLE_VALUE,
      FundRuleName.DIVIDEND_STRATEGY,
      FundRuleName.ESG_STRATEGY,
      FundRuleName.FACTOR_DRIVEN_STRATEGY,
      FundRuleName.SECTOR_SPECIALITY_STRATEGY,
    ])
  }

  setFiltersGroupState(filterGroupState: FilterGroupStateWrapper) {
    let interpretedFilterState: any
    if (filterGroupState) {
      this.latestFilterSelection = filterGroupState.getFilterGroupName()
      interpretedFilterState = this.interpretFilterState(
        filterGroupState.getJsonFilterGroupState(),
      )
      this.consolidatedFilterStateMap.set(
        filterGroupState.getFilterGroupName(),
        interpretedFilterState.appliedFilterAry,
      )
      this._uiFilterSelectionMap.set(
        filterGroupState.getFilterGroupName(),
        interpretedFilterState.uiFilterCategoryAry,
      )
      if (
        !this.consolidatedFilterStateMap.get(
          FilterGroup.AssetClasFilterGroup,
        ) ||
        this.consolidatedFilterStateMap.get(FilterGroup.AssetClasFilterGroup)
          .length === 0
      ) {
        this.initializeAssetFilterSelection()
      }
      this.handleBenchMarkAndETFScenario()
    }
  }

  getConsolidatedFilterGroupStateMap(): Map<FilterGroup, string[]> {
    return this.consolidatedFilterStateMap
  }

  setSelectedDisplayType(selectedDisplayType: string) {
    this.selectedDisplayType = selectedDisplayType
    this.latestFilterSelection = selectedDisplayType
  }

  setBenchMarkSelected(isBenchMarkSelected: boolean) {
    this.isBenchMarkSelected = isBenchMarkSelected
    this.latestFilterSelection = 'benchMark'
  }

  getBenchMarkFlagSelection(): boolean {
    return this.isBenchMarkSelected
  }

  public setAllShareClassSelected(isAllShareClassSelected: boolean) {
    this.isAllShareClassSelected = isAllShareClassSelected
    this.latestFilterSelection = 'shareClass'
  }

  getAllShareClassFlagSelection(): boolean {
    return this.isAllShareClassSelected
  }

  getSelectedDisplayType(): string {
    return this.selectedDisplayType
  }

  getUIFilterSelectionMap(): Map<FilterGroup, string[]> {
    return this._uiFilterSelectionMap
  }

  interpretFilterState(jsonFilterState: any): any {
    const uiFilterCategoryAry: string[] = [],
      appliedFilterCategoryAry: string[] = [],
      consolidatedAssetTreeNode: FilterNode = new FilterNode({})

    // Convert to tree structure
    consolidatedAssetTreeNode.addChildren(
      FilterSelectionInterpreterUtility.generateTreeStruct(jsonFilterState),
    )

    // Reduce selection
    FilterSelectionInterpreterUtility.traverseTree(
      consolidatedAssetTreeNode,
      (filterNode: FilterNode) => {
        if (filterNode.readData().isSelected && filterNode.getParentNode()) {
          filterNode.getParentNode().readData().isSelected = false
        }
      },
    )

    FilterSelectionInterpreterUtility.traverseTree(
      consolidatedAssetTreeNode,
      (filterNode: FilterNode) => {
        if (filterNode.readData().isSelected) {
          uiFilterCategoryAry.push(filterNode.readData().id)
        }
      },
    )

    //  Extract applied filter categories
    FilterSelectionInterpreterUtility.traverseTree(
      consolidatedAssetTreeNode,
      (filterNode: FilterNode) => {
        if (filterNode.readData().isSelected && filterNode.getParentNode()) {
          FilterSelectionInterpreterUtility.traverseTree(
            filterNode,
            (currentFilterNode: FilterNode) => {
              if (
                !currentFilterNode.getChildren() ||
                currentFilterNode.getChildren().length === 0
              ) {
                appliedFilterCategoryAry.push(
                  currentFilterNode.readData().ruleName,
                )
              }
            },
          )
        }
      },
    )
    return {
      appliedFilterAry: appliedFilterCategoryAry,
      uiFilterCategoryAry: uiFilterCategoryAry,
    }
  }

  getLatestFilterSelection() {
    return this.latestFilterSelection
  }
  handleBenchMarkAndETFScenario() {
    const filterStateWrapper: UnmodifiableFilterStateWrapper =
      new UnmodifiableFilterStateWrapper()
    filterStateWrapper.setFilterState(this)
    this.overrideShareClassFlag = false
    if (
      !UtilityService.isAllShareClassOptionApplicable(
        <IFilterState>filterStateWrapper,
      )
    ) {
      this.overrideShareClassFlag = true
    }
  }
}
